import React, { Fragment, useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import { IconEdit, IconMessage, IconMessageShare } from '@tabler/icons-react'
import { Switch, Tooltip } from 'antd'
import { formatDistanceToNowStrict } from 'date-fns'
import { sortBy } from 'lodash'
import styled from 'styled-components'

import { Avatar } from './Avatar'
import { Button } from './BUI/Button'
import { Card } from './BUI/Card'
import { Modal } from './BUI/Modal'
import { Popover, PopoverItemNaked } from './BUI/Popover'
import { Text as BuiText, Text } from './BUI/Text'
import { DozerLoading } from './DozerLoading'
import { Flexbox } from './Flexbox'
import { TextWithHyperlinks } from './TextWithHyperlinks'
import { UserInfoOverlay } from './UserInfoOverlay'

import { AttachmentBoxes } from '@/components/shared-components/AttachmentBox'
import { useLocale, LocaleContextValue } from '@/context/localeContext'
import { MentionDisplay, MentionType } from '@/context/mentionInputContext'
import { useTimezonedDates } from '@/context/timezoneContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import {
  consolidateAuditLogs,
  consolidateMessageLogs,
} from '@/modules/audits/consolidateAuditLogs'
import { useAuditLogDiff } from '@/modules/audits/getAuditLogDiff'
import { GET_KNOCK_MESSAGE_CONTENT } from '@/modules/conversationThreads/graphQL'
import { getAuditLogName, getAssigneeName } from '@/utils/getName'

import { buildPassTheme } from 'src/styles/theme'

import {
  KnockMessageContentUiQuery,
  KnockMessageContentUiQueryVariables,
} from 'types/api.graphql'
import {
  AttachmentLike,
  AuditLogLike,
  ConversationLike,
  KnockMessageLogLike,
} from 'types/vagueTypes'

const ScrollableConversation = styled.div<{ maxHeight?: string }>`
  width: 100%;
  max-height: ${({ maxHeight }) => maxHeight ?? '600px'};
  overflow: auto;
  display: flex;
  flex-direction: column-reverse;
  gap: 0.25rem;
`

const Wrapper = styled.div<{ hasMessages: boolean }>`
  width: 100%;
  background-color: ${({ theme }) => theme.colour.neutral.surface};
  border: 1px solid ${({ theme }) => theme.colour.secondary._100};
  padding: 1rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: flex;
  flex-direction: column;
  gap: ${({ hasMessages }) => (hasMessages ? '2rem' : '0')};

  ${({ theme: { media } }) => media.md`
    padding: 2rem 1.5rem;
  `}
`

const Line = styled.span`
  flex-shrink: 0;
  display: block;
  width: 1px;
  height: 0.5rem;
  margin-left: 0.375rem;
  background-color: ${({ theme }) => theme.colour.secondary._100};
`

const Content = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background-color: ${({ theme }) => theme.colour.neutral.surface};
  padding: 0.75rem;
`

const getAuditLogContent = (
  auditLog: AuditLogLike,
  translate: LocaleContextValue['t'],
  getAuditLogDiff: ReturnType<typeof useAuditLogDiff>['getAuditLogDiff']
) => {
  const [name] = getAuditLogName(auditLog)
  const diff = getAuditLogDiff(auditLog, translate, false)

  if (!diff.length) return null

  return (
    <Flexbox flexDirection="column" alignItems="flex-start">
      {diff.map((d) => {
        const nameContent = (
          <Text variant="caption01" secondary="base" stronger>
            {name}{' '}
          </Text>
        )

        const content = (
          <Text variant="caption01" secondary="_500">
            {d.verb.toLowerCase()} {d.conjunction}{' '}
            <Text variant="caption01" stronger secondary="base">
              {d.key}
            </Text>{' '}
            {d.newValue && <>to {d.newValue}</>}
          </Text>
        )

        if (!d.oldValue)
          return (
            <span key={d.key}>
              {nameContent}
              {content}
            </span>
          )

        return (
          <span key={d.key} style={{ cursor: 'pointer' }}>
            <Text variant="caption01" secondary="base" stronger>
              {name}{' '}
            </Text>

            <Popover
              content={
                <PopoverItemNaked>
                  {d.oldValue && (
                    <>
                      <Text variant="caption01" secondary="_400" block>
                        From
                      </Text>
                      <Text variant="caption01">{d.oldValue}</Text>
                    </>
                  )}
                  {d.newValue && (
                    <>
                      <Text variant="caption01" secondary="_400" block>
                        To
                      </Text>
                      <Text variant="caption01">{d.newValue}</Text>
                    </>
                  )}
                </PopoverItemNaked>
              }
            >
              {content}
            </Popover>
          </span>
        )
      })}
    </Flexbox>
  )
}

const KnockMessagePreview = ({ logId }) => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null)

  const [showModal, setShowModal] = useState(false)
  const [mode, setMode] = useState<'TEXT' | 'HTML' | null>(null)

  const { data, loading } = useQuery<
    KnockMessageContentUiQuery,
    KnockMessageContentUiQueryVariables
  >(GET_KNOCK_MESSAGE_CONTENT, {
    variables: { logId },
    skip: !showModal,
    onCompleted: (data) => {
      if (data?.knockMessageContent_admin?.html_body) {
        const doc = iframeRef.current.contentWindow?.document

        if (doc) {
          doc.open()
          doc.write(data.knockMessageContent_admin.html_body)
          doc.close()

          iframeRef.current.style.height =
            iframeRef.current.contentWindow.document.body.scrollHeight + 'px'

          setMode('HTML')
        }
      } else {
        setMode('TEXT')
      }
    },
  })

  return (
    <>
      <IconMessageShare
        size={16}
        onClick={() => setShowModal(true)}
        style={{ cursor: 'pointer' }}
      />
      <Modal
        width={data?.knockMessageContent_admin?.html_body ? 800 : 600}
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={<Button onClick={() => setShowModal(false)}>Close</Button>}
      >
        <DozerLoading relative noBackground loading={loading} />

        {data?.knockMessageContent_admin?.to && (
          <>
            <Text variant="caption01" mb="0.25rem">
              To
            </Text>
            <Content>{data?.knockMessageContent_admin?.to}</Content>
            <br />
          </>
        )}

        {data?.knockMessageContent_admin?.subject && (
          <>
            <Text variant="caption01" mb="0.25rem">
              Subject
            </Text>
            <Content>{data?.knockMessageContent_admin?.subject}</Content>
            <br />
          </>
        )}

        {!loading && (
          <Text variant="caption01" mb="0.25rem">
            Message
          </Text>
        )}

        <iframe
          width="100%"
          src="about:blank"
          ref={iframeRef}
          style={{
            border: 'none',
            display: mode === 'TEXT' ? 'none' : 'initial',
          }}
        />

        {mode === 'TEXT' && data?.knockMessageContent_admin?.text_body ? (
          <Content>
            <TextWithHyperlinks
              content={
                data.knockMessageContent_admin.text_body ?? 'No message found'
              }
            />
          </Content>
        ) : null}
      </Modal>
    </>
  )
}

const REGEX = /{{(.*?):(.*?):(.*?)}}/g // {{type:id:name}}

export const formatMentions = (content: string): React.ReactNode => {
  const mentionChunks = Array.from(content?.matchAll(REGEX) ?? [])

  if (!mentionChunks.length) return content

  let charIndex = 0

  return (
    <>
      {mentionChunks.map((mentionChunk) => {
        const [fullMatch, type, id, name] = mentionChunk
        const currCharIndex = charIndex

        charIndex = mentionChunk.index + fullMatch.length

        return (
          <>
            {content.slice(currCharIndex, mentionChunk.index)}
            {type === MentionType.AdminUser && (
              <UserInfoOverlay mode="ADMIN_USER" id={id} noIcon>
                {name}
              </UserInfoOverlay>
            )}
            {type === MentionType.Text && (
              <MentionDisplay>{name}</MentionDisplay>
            )}
          </>
        )
      })}
      {content.slice(charIndex)}
    </>
  )
}

type Message = {
  id: string
  type: 'MESSAGE' | 'AUDIT_LOG' | 'MESSAGE_LOG'
  icon?: React.ReactNode
  createdAt: string
  name: string
  content: React.ReactNode
  attachments?: AttachmentLike[]
}

type MessageProps = {
  message: Message
  isMobile: boolean
}

const MessageRow: React.FC<MessageProps> = ({ message, isMobile }) => {
  const { formatDateString } = useTimezonedDates()

  const dateContent = (
    <Tooltip
      title={formatDateString({ date: message.createdAt })}
      trigger={['hover']}
    >
      <Text
        variant="caption01"
        secondary="_500"
        style={{
          marginLeft: message.type !== 'MESSAGE' && isMobile ? '1.75rem' : null,
        }}
      >
        {formatDistanceToNowStrict(new Date(message.createdAt))} ago
      </Text>
    </Tooltip>
  )

  if (message.type === 'MESSAGE') {
    return (
      <Card
        style={{
          marginTop: '0.25rem',
          marginBottom: '0.25rem',
          boxShadow: '0px 2px 1px rgba(0,0,0,0.1)',
        }}
      >
        <Flexbox alignItems="center" gap="0.5rem">
          <Avatar name={message.name} size={18} fontSize="0.5rem" />

          <Text stronger variant="caption01">
            {message.name}
          </Text>

          {dateContent}
        </Flexbox>

        <Text
          mt="0.5rem"
          mb={message.attachments?.length ? '0.5rem' : undefined}
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {formatMentions(message.content as string)}
        </Text>

        <AttachmentBoxes
          width={isMobile ? 40 : 80}
          height={isMobile ? 40 : 80}
          attachments={message.attachments?.map((a) => ({
            uid: a.id,
            previewUrl: a.url,
            url: a.thumbnail?.small || a.url,
            alt: a.fileName,
          }))}
        />
      </Card>
    )
  }

  return (
    <>
      <Flexbox alignItems="center" gap="0.25rem">
        <Flexbox style={{ flexShrink: 0 }}>{message.icon}</Flexbox>
        &nbsp;
        <Text variant="caption01" secondary="_500">
          {message.content}
        </Text>
        {!isMobile && (
          <>
            <span> ∙ </span>
            {dateContent}
          </>
        )}
      </Flexbox>
      {isMobile && dateContent}
    </>
  )
}

type Props = {
  conversation: ConversationLike
  auditLogs?: AuditLogLike[]
  messageLogs?: KnockMessageLogLike[]
  card?: boolean
  cardTitle?: string
  maxHeight?: string
  forceMobile?: boolean
  topOfScrollContent?: React.ReactNode
  children?: React.ReactNode
}

export type ConversationThreadProps = Props

export const ConversationThread: React.FC<Props> = ({
  conversation,
  auditLogs,
  messageLogs,
  card,
  cardTitle = 'Activity',
  maxHeight,
  forceMobile,
  topOfScrollContent,
  children,
}) => {
  const { getAuditLogDiff } = useAuditLogDiff()
  const smallDevice = useBreakpoint('md')
  const { t } = useLocale()

  const isMobile = forceMobile ?? smallDevice

  const [showAudits, setShowAudits] = useState(true)

  const consolidatedLogs = useMemo(() => {
    if (!auditLogs?.length) return null
    return consolidateAuditLogs(sortBy(auditLogs, 'createdAt'))
  }, [auditLogs])

  const consolidateMessages = useMemo(() => {
    if (!messageLogs?.length) return null
    return consolidateMessageLogs(sortBy(messageLogs, 'logDate'))
  }, [messageLogs])

  const allMessages = useMemo(() => {
    if (
      !conversation?.messages?.length &&
      !consolidatedLogs?.length &&
      !consolidateMessages?.length
    ) {
      return []
    }

    const messages: Message[] = []

    conversation?.messages.forEach((message) => {
      const [name] = getAssigneeName(message.sender)

      messages.push({
        id: message.id,
        name,
        content: message.content,
        createdAt: message.createdAt,
        attachments: message.attachments,
        type: 'MESSAGE',
      })
    })

    consolidateMessages?.forEach((log) => {
      messages.push({
        id: log.id,
        content: (
          <Flexbox alignItems="center" gap="0.5rem">
            <Text variant="caption01">
              Message {log.status.toLowerCase()} to{' '}
              <Text secondary="base" variant="caption01" stronger>
                {log.sentTo.name}
              </Text>
            </Text>
            <KnockMessagePreview logId={log.id} />
          </Flexbox>
        ),
        createdAt: log.logDate,
        attachments: [],
        name: log.sentTo.name,
        type: 'MESSAGE_LOG',
        icon: (
          <IconMessage size={16} color={buildPassTheme.colour.secondary._400} />
        ),
      })
    })

    if (showAudits) {
      consolidatedLogs?.forEach((log, index) => {
        const content = getAuditLogContent(log, t, getAuditLogDiff)

        if (!content) return

        const [name] = getAuditLogName(log)

        messages.push({
          id: log.id + '-' + index,
          name,
          content,
          createdAt: log.createdAt,
          attachments: [],
          type: 'AUDIT_LOG',
          icon: (
            <IconEdit size={16} color={buildPassTheme.colour.secondary._400} />
          ),
        })
      })
    }
    return sortBy(messages, 'createdAt').reverse()
  }, [
    conversation?.messages,
    consolidatedLogs,
    consolidateMessages,
    showAudits,
    getAuditLogDiff,
    t,
  ])

  if (!conversation?.messages?.length && !allMessages.length) return null

  const showDetailsSwitch =
    !!auditLogs?.length && !!conversation?.messages?.length

  const Container = ({ children, ...rest }) =>
    card ? <Card {...rest}>{children}</Card> : <>{children}</>

  return (
    <Container bordered title={cardTitle}>
      {showDetailsSwitch && (
        <Flexbox
          justifyContent="flex-end"
          alignItems="center"
          gap="0.5rem"
          mb="0.25rem"
        >
          <BuiText variant="caption01" secondary="_600">
            Show details
          </BuiText>
          <Switch size="small" checked={showAudits} onChange={setShowAudits} />
        </Flexbox>
      )}

      <Wrapper hasMessages={allMessages.length > 0}>
        <ScrollableConversation maxHeight={maxHeight}>
          {allMessages.map((message, index) => {
            const isMessage = message.type === 'MESSAGE'

            return (
              <Fragment key={message.id}>
                {!!index && (isMessage ? <span /> : <Line />)}
                <MessageRow message={message} isMobile={isMobile} />
              </Fragment>
            )
          })}
          {/* due to the reverse layout this needs to be last */}
          {topOfScrollContent}
        </ScrollableConversation>
        {children}
      </Wrapper>
    </Container>
  )
}
